"use client";

import React, { Suspense, lazy, useEffect, useState } from "react";
import Image from "next/image";

/**
 * LazyDotLottie - A lightweight lazy-loaded DotLottie animation component
 *
 * DotLottie is significantly smaller than traditional Lottie JSON files and provides
 * better performance. This component lazy loads the DotLottie player to avoid
 * impacting initial page load performance.
 *
 * Features:
 * - Lazy loading to reduce initial bundle size
 * - Automatic fallback to static image if animation fails to load
 * - Suspense boundary for smooth loading experience
 * - TypeScript support with proper prop types
 * - Hardware-accelerated rendering
 *
 * @example
 * ```tsx
 * <LazyDotLottie
 *   src="/animations/my-animation.lottie"
 *   fallbackSrc="/images/fallback.png"
 *   width={200}
 *   height={200}
 *   alt="My animation"
 * />
 * ```
 */

// Lazy load the DotLottie component to avoid loading it on initial page load
const DotLottieReact = lazy(() =>
  import("@lottiefiles/dotlottie-react").then((module) => ({
    default: module.DotLottieReact,
  }))
);

interface LazyDotLottieProps {
  src: string;
  fallbackSrc: string;
  className?: string;
  width?: number;
  height?: number;
  alt?: string;
  loop?: boolean;
  autoplay?: boolean;
  speed?: number;
  mode?: "forward" | "reverse" | "bounce" | "reverse-bounce";
  backgroundColor?: string;
  renderConfig?: {
    devicePixelRatio?: number;
  };
}

export const LazyDotLottie = ({
  src,
  fallbackSrc,
  className = "",
  width = 150,
  height = 150,
  alt = "Animation",
  loop = true,
  autoplay = true,
  speed = 0.25,
  mode = "forward",
  backgroundColor = "transparent",
  renderConfig = { devicePixelRatio: 2 },
}: LazyDotLottieProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Preload the DotLottie file
  useEffect(() => {
    const preloadDotLottie = async () => {
      try {
        // Simple fetch to check if the file exists and can be loaded
        const response = await fetch(src, { method: "HEAD" });
        if (response.ok) {
          setIsLoaded(true);
        } else {
          throw new Error("Failed to load DotLottie file");
        }
      } catch (error) {
        console.warn("Failed to preload DotLottie animation:", error);
        setHasError(true);
        setIsLoaded(true); // Still mark as loaded to show fallback
      }
    };

    preloadDotLottie();
  }, [src]);

  // Fallback image
  const FallbackImage = () => (
    <Image
      src={fallbackSrc}
      className={className}
      width={width}
      height={height}
      alt={alt}
    />
  );

  // If not loaded yet or has error, show fallback
  if (!isLoaded || hasError) {
    return <FallbackImage />;
  }

  // Show DotLottie animation with Suspense fallback
  return (
    <Suspense fallback={<FallbackImage />}>
      <DotLottieReact
        src={src}
        className={className}
        loop={loop}
        autoplay={autoplay}
        speed={speed}
        mode={mode}
        backgroundColor={backgroundColor}
        renderConfig={renderConfig}
        style={{
          width: width,
          height: height,
        }}
      />
    </Suspense>
  );
};
